import { useEffect, useState } from 'react'
import { Flex, Text, Button, Tab, Spacer, Modal } from '@jisr-hr/design-system'
import { formatDistanceToNow, isToday } from 'date-fns'
import { arSA, enUS } from 'date-fns/locale'
import { Trans } from 'react-i18next'
import cn from 'classnames'
import _, { omitBy } from 'lodash'
import { Avatar } from '@material-ui/core'
import I18n from 'translations/i18n'
import { useDispatch, useSelector } from 'utils/hooks'
import FAICon from 'components/fontawesome/FAICon'
import { notificationsLoad, resetNotifications } from 'redux/appNotifications/actionCreators'
import { TNotification } from 'redux/appNotifications/reducer'
import {
  markAllAsRead,
  unreadNotificationsLoad,
} from 'redux/appNotifications/markAsRead/actionCreators'
import {
  approvalDetailReset,
  taskDetailLoad,
} from 'redux/requests/newRequestsApproval/actionCreators'
import { getAllEmployeesLoad } from 'redux/actions/employeeActions'
import JisrLoader from 'components/global/JisrLoader'
import Lazyload, { Scrolling } from 'components/global/atoms/Lazyload'
import RequestDrawer from 'components/global/templates/RequestDrawer'
import RequestDetails from 'containers/authorised/requests/components/RequestDetails'
import { generateRandomColor } from 'containers/authorised/setting/v2/Attendance/ShiftsWorkingHours/helper'
import { labels } from 'utils/whitelabel'
import { isRtl } from 'utils/uiHelpers'
import NotificationsSettings from './NotificationsSettings'
import NotificationsGuide from '../NotificationsGuide'
import styles from '../styles.module.css'
import { TTab } from '../types'
import { isChrome, isEdge, isFirefox, isSafari } from './helper'
import AnnouncementModal from './AnnouncementModal'

const locales = { en: enUS, ar: arSA }
const LOCALE = localStorage.getItem('Locale') ?? 'en'

const NOTIFICATION_ICON = {
  new_joining: 'user-plus',
  birthday: 'cake-candles',
  anniversary: 'party-horn',
  admin_announcement: 'bullhorn',
}

const NoNotifications = (): JSX.Element => (
  <Flex
    itemsCenter
    justifyCenter
    flexCol
    style={{ height: 'calc(100vh - 162px)', gap: 16 }}
  >
    <Flex
      itemsCenter
      justifyCenter
      className={styles.nodataIconContainer}
    >
      <FAICon
        icon={['far', 'check']}
        size="lg"
      />
    </Flex>
    <Text
      typography="paragraph16"
      weight="bold"
    >
      {I18n.t('no_new_notifications')}
    </Text>
  </Flex>
)

const NotificationItem = ({
  title,
  message,
  created_at,
  category,
  employee,
  is_read,
  data,
  testId,
  handleView,
  openAnnouncement,
}: TNotification): JSX.Element => {
  const isRequest = [
    'visible_approval',
    'request_approved',
    'request_rejected',
    'request_expired',
  ].includes(category)

  const isAnnouncement = ['admin_announcement', 'automation_announcement'].includes(category)

  const isTask = category === 'task'
  const onClick = (): void | undefined => {
    if (handleView && isRequest && data?.employee_id) handleView()
    else if (handleView && isTask) handleView()
    else if (openAnnouncement && isAnnouncement) openAnnouncement()
    return undefined
  }

  return (
    <Lazyload fallback={(): JSX.Element => <>{I18n.t('loading')}</>}>
      <Flex
        className={cn(
          styles.notificationItem,
          { [styles.unread]: !is_read },
          { [styles.pointer]: (data?.employee_id && isRequest) || isTask || isAnnouncement },
        )}
        style={{ gap: 16 }}
        data-testid={testId}
        onClick={onClick}
      >
        <Flex
          itemsCenter
          justifyCenter
          className={cn({
            [styles.imgContainer]: !isRequest && !isTask,
          })}
        >
          {isRequest || isTask ? (
            <Avatar
              src={employee?.avatar_thumb}
              alt={employee?.full_name_i18n}
              style={{ width: '41px', height: '41px', backgroundColor: generateRandomColor('') }}
              className="jisr-lazy-avatar"
            />
          ) : (
            <FAICon
              icon={['far', NOTIFICATION_ICON[category] ?? 'bell']}
              color="var(--color-natural-gray-darker-1-new)"
              size="xl"
            />
          )}
        </Flex>
        <div style={{ flex: 1 }}>
          <Text
            typography="paragraph16"
            dangerouslySetInnerHTML={{
              __html: title ?? message,
            }}
          />
          <Text
            typography="paragraph16"
            color="var(--color-natural-gray-default-new)"
          >
            {formatDistanceToNow(new Date(created_at), {
              addSuffix: true,
              locale: locales[LOCALE],
            })}
          </Text>
        </div>
      </Flex>
    </Lazyload>
  )
}

export default function NotificationsPanel(): JSX.Element {
  const [filters, setFilters] = useState<{ page: number; rpp: number; old_notifications: boolean }>(
    {
      page: 1,
      rpp: 10,
      old_notifications: false,
    },
  )
  const [activeTab, setActiveTab] = useState<TTab>({
    label: I18n.t('all'),
    key: 'all',
    testId: 'all',
  })
  const [openSettings, setOpenSettings] = useState(false)
  const [openGuide, setOpenGuide] = useState(false)
  const [activeRequest, setActiveRequest] = useState<{
    employee_id?: number
    request_type?: string
    request_id?: number
    request_type_name?: string
  } | null>(null)
  const [announcement, setAnnouncement] = useState<TNotification | null>(null)

  const dispatch = useDispatch()
  const {
    notifications,
    pagination,
    total_pending_team_requests,
    has_old_notification,
    is_old_notifications,
    fetching,
  } = useSelector(({ appNotifications }) => appNotifications)
  const { unread: isUnread, fetching: fetchingUnread } = useSelector(({ markAsRead }) => markAsRead)

  const { todayList = [], earlierList = [] } = _.groupBy(notifications, (obj) =>
    isToday(new Date(obj.created_at)) ? 'todayList' : 'earlierList',
  )

  const tabs: TTab[] = [
    {
      label: I18n.t('all'),
      key: 'all',
      testId: 'all',
    },
    {
      label: I18n.t('requests_and_tasks'),
      key: 'requests_and_tasks',
      ...(total_pending_team_requests && { count: total_pending_team_requests }),
      testId: 'requests_and_tasks_tab',
      category: ['requests'],
    },
    {
      label: I18n.t('events'),
      key: 'updates',
      testId: 'updates_tab',
      category: ['shift_schedule_changed', 'events'],
    },
    {
      label: I18n.t('announcements'),
      key: 'announcements',
      testId: 'announcements_tab',
      category: ['announcements'],
    },
  ]

  const path = window.location.hash?.split?.('#/')?.[1] || ''
  const isHomeTasks = !path || path.includes('/tasks')

  const fetchData = (): void => {
    const pureFilters = omitBy(filters, (v) => v === false)
    dispatch(
      notificationsLoad({
        all: true,
        category: activeTab?.category,
        ...pureFilters,
      }),
    )
    dispatch(unreadNotificationsLoad())
  }

  const handleTabChange = (v: TTab): void => {
    setActiveTab(v)
    setFilters({
      ...filters,
      page: 1,
      old_notifications: false,
    })
    dispatch(resetNotifications())
  }

  const guideContent = (): JSX.Element => {
    const chromeGuide = (
      <Text
        typography="paragraph16"
        color="var(--gray)"
      >
        <Trans
          i18nKey="chrome_notifications_guide"
          components={{
            br: <br />,
            b: <b />,
            Dots: (
              <FAICon
                icon={['far', 'ellipsis-vertical']}
                color="var(--color-natural-black-default)"
                fontSize="larger"
              />
            ),
            whiteLabel: labels.name,
          }}
        />
      </Text>
    )

    const edgeGuide = (
      <Text
        typography="paragraph16"
        color="var(--gray)"
      >
        <Trans
          i18nKey="edge_notifications_guide"
          components={{
            br: <br />,
            b: <b />,
            Dots: (
              <FAICon
                icon={['far', 'ellipsis']}
                color="var(--color-natural-black-default)"
                fontSize="larger"
              />
            ),
            whiteLabel: labels.name,
          }}
        />
      </Text>
    )

    const firefoxGuide = (
      <Text
        typography="paragraph16"
        color="var(--gray)"
      >
        <Trans
          i18nKey="firefox_notifications_guide"
          components={{
            br: <br />,
            b: <b />,
            Bars: (
              <FAICon
                icon={['far', 'bars']}
                color="var(--color-natural-black-default)"
                fontSize="larger"
              />
            ),
            whiteLabel: labels.name,
          }}
        />
      </Text>
    )

    const safariGuide = (
      <Text
        typography="paragraph16"
        color="var(--gray)"
      >
        <Trans
          i18nKey="safari_notifications_guide"
          components={{
            br: <br />,
            b: <b />,
            Apple: (
              <FAICon
                icon={['fab', 'apple']}
                fontSize="larger"
              />
            ),
            whiteLabel: labels.name,
          }}
        />
      </Text>
    )

    if (isEdge) {
      return edgeGuide
    } else if (isChrome) {
      return chromeGuide
    } else if (isFirefox) {
      return firefoxGuide
    } else if (isSafari) {
      return safariGuide
    }
    return <></>
  }

  const loadRequestDetail = (
    request_id?: number,
    employee_id?: number,
    request_type?: string,
    request_type_ar?: string,
    request_type_en?: string,
  ): void => {
    if (employee_id) {
      setActiveRequest({
        employee_id,
        request_type,
        request_id,
        request_type_name: isRtl ? request_type_ar : request_type_en,
      })
    } else {
      dispatch(taskDetailLoad(request_id))
    }
  }

  useEffect(() => {
    fetchData()
  }, [activeTab?.key, filters])

  useEffect(() => {
    if (has_old_notification) {
      setFilters({
        ...filters,
        page: 1,
        ...((has_old_notification || is_old_notifications) && { old_notifications: true }),
      })
    }
  }, [has_old_notification])

  useEffect(() => {
    if (activeTab?.key === 'all') dispatch(getAllEmployeesLoad())
  }, [activeTab?.key])

  return (
    <>
      <div className={styles.header}>
        <Flex
          justifyBetween
          itemsCenter
        >
          <Text
            typography="title"
            weight="bold"
          >
            {I18n.t('notifications')}
          </Text>
          <Button
            label={I18n.t('mark_all_as_read')}
            onClick={(): void => {
              dispatch(markAllAsRead()).then(fetchData)
            }}
            startIcon={
              <FAICon
                icon={['far', 'check']}
                size="xs"
              />
            }
            disabled={!isUnread || fetchingUnread}
            variant="tertiary"
            size="medium"
            data-testid="mark_all_as_read"
          />
        </Flex>
        <Spacer height={26} />
        <Flex
          justifyBetween
          itemsCenter
          className={styles.tabs}
        >
          <Tab
            tabs={tabs}
            active={activeTab?.key}
            onChange={handleTabChange}
          />
          <FAICon
            icon={['far', 'gear']}
            size="lg"
            className={styles.settingIcon}
            color="var(--color-natural-black-light-new)"
            onClick={(): void => setOpenSettings(true)}
            data-testid="open_settings"
          />
        </Flex>
      </div>
      <div className={styles.content}>
        <Scrolling
          className="scroll-y scroll-x"
          style={{ maxHeight: '100%' }}
          {...(pagination?.next_page && {
            onScrollEnd: (): void =>
              setFilters({
                ...filters,
                page: pagination?.next_page,
                ...((has_old_notification || is_old_notifications) && { old_notifications: true }),
              }),
          })}
        >
          {!fetching && !notifications.length && <NoNotifications />}
          {fetching && <JisrLoader absolute />}

          {!!todayList.length && (
            <Text
              typography="paragraph16"
              color="var(--color-natural-gray-dark-new)"
              style={{ marginInline: 24, marginTop: 16 }}
            >
              {I18n.t('today')}
            </Text>
          )}
          {todayList.map((data) => (
            <NotificationItem
              key={data.id}
              handleView={(): void => {
                if (data.category === 'task') {
                  loadRequestDetail(data.data?.id)
                } else {
                  loadRequestDetail(
                    data.data?.id,
                    data.data?.employee_id,
                    data.data?.class_name,
                    data.data?.resource_type_ar,
                    data.data?.resource_type_en,
                  )
                }
              }}
              testId="notification_item"
              openAnnouncement={(): void => {
                if (['admin_announcement', 'automation_announcement'].includes(data.category))
                  setAnnouncement(data)
              }}
              {...data}
            />
          ))}

          {!!earlierList.length && (
            <Text
              typography="paragraph16"
              color="var(--color-natural-gray-dark-new)"
              style={{ marginInline: 24, marginTop: 16 }}
            >
              {I18n.t('earlier')}
            </Text>
          )}
          {earlierList.map((data) => (
            <NotificationItem
              key={data.id}
              handleView={(): void => {
                if (data.category === 'task') {
                  loadRequestDetail(data.data?.id)
                } else {
                  loadRequestDetail(
                    data.data?.id,
                    data.data?.employee_id,
                    data.data?.class_name,
                    data.data?.resource_type_ar,
                    data.data?.resource_type_en,
                  )
                }
              }}
              testId="notification_item"
              openAnnouncement={(): void => {
                if (['admin_announcement', 'automation_announcement'].includes(data.category))
                  setAnnouncement(data)
              }}
              {...data}
            />
          ))}
        </Scrolling>
      </div>

      <Modal
        isOpen={openSettings}
        width={460}
        variant="centerDialog"
        header={
          <Text
            typography="title"
            weight="bold"
            data-testid="notifications_settings_header"
          >
            {I18n.t('browser_notifications')}
          </Text>
        }
        onClose={(): void => setOpenSettings(false)}
        modalHeaderStyle={{
          width: '100%',
          textAlign: 'center',
        }}
        modalContentStyle={{ padding: 24 }}
        content={<NotificationsSettings setOpenGuide={setOpenGuide} />}
      />
      <NotificationsGuide
        open={openGuide}
        guideContent={guideContent()}
        onClose={(): void => setOpenGuide(false)}
      />
      <RequestDrawer
        needApproval
        actionCallback={(): void => {
          setActiveRequest(null)
        }}
        active={!!activeRequest}
        requestType={activeRequest?.request_type}
        onClose={(): void => setActiveRequest(null)}
        vars={activeRequest}
        hideFooter
      />
      {!isHomeTasks && (
        <RequestDetails actionCallback={(): Promise<void> => dispatch(approvalDetailReset())} />
      )}
      <AnnouncementModal
        open={!!announcement}
        onClose={(): void => {
          setAnnouncement(null)
        }}
        title={announcement?.title}
        description={announcement?.message}
        date={announcement?.created_at}
        attachment={announcement?.attachment}
      />
    </>
  )
}
